import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  useTheme,
  Skeleton,
  Box,
  Link,
  Container,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Avatar,
  LinearProgress,
  Tooltip,
  DialogActions,
  Modal
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import api, { baseURL } from '../services/api';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import { fetchCurrentUser } from '../services/auth';
import { useBranding } from '../context/BrandingContext';
import creatorLogo from '../assets/LOGO2MPSERVICESFORPROFESSIONALPERFORM.png';
import {
  getCustomFieldsMetadata,
  getEmployeeCustomFields,
  updateEmployeeCustomFields,
} from '../services/customFields';
import { TextField, Snackbar, Alert } from '@mui/material';
import { listPayslips } from '../services/payslips';
import { upcomingHolidaysCached } from '../services/holidays';
import { reviewQueueLeaves, allLeaves } from '../services/leaves';

const DashboardPage = () => {
  const [stats, setStats] = useState({
    missions: 0,
    sorties: 0,
    employees: 0,
    absences: 0,
    contrats: 0,
    decharges: 0,
    leaves: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const [userRole, setUserRole] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedPdf, setSelectedPdf] = useState(null); // State for selected PDF
  const [documentLegals, setDocumentLegals] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const [errorDocuments, setErrorDocuments] = useState(null);
  // Custom fields (user)
  const [cfMeta, setCfMeta] = useState({});
  const [cfValues, setCfValues] = useState({});
  const [cfLoading, setCfLoading] = useState(false);
  const [cfSaving, setCfSaving] = useState(false);
  const [cfInitial, setCfInitial] = useState({});
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  // Payslips (employee)
  const [payslips, setPayslips] = useState([]);
  const [slipsLoading, setSlipsLoading] = useState(false);
  // Holidays
  const [holidaysCache, setHolidaysCache] = useState({ updatedAt: null, days: 7, items: [] });
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  // Leaves review queue (observation to do)
  const [reviewCount, setReviewCount] = useState(0);
  const [reviewLoading, setReviewLoading] = useState(false);


  useEffect(() => {
    const loadUser = async () => {
      const currentUserObject = await fetchCurrentUser(true); // Force refresh pour éviter le cache
      if (currentUserObject) {
        setUserRole(currentUserObject.role);
        setEmployee(currentUserObject.employee);
      }
      setLoading(false);
    };
    loadUser();
  }, []);

  useEffect(() => {
    const loadCustomFields = async () => {
      if (!employee?.id) return;
      try {
        setCfLoading(true);
        const [metaRes, fieldsRes] = await Promise.all([
          getCustomFieldsMetadata(),
          getEmployeeCustomFields(employee.id),
        ]);
        const meta = metaRes.data || {};
        const vals = fieldsRes.data || {};
        const initial = {};
        Object.keys(meta).forEach((k) => {
          initial[k] = vals[k]?.value ?? '';
        });
        setCfMeta(meta);
        setCfValues(initial);
        setCfInitial(initial);
      } catch (e) {
        console.error('Erreur chargement champs personnalisés:', e);
      } finally {
        setCfLoading(false);
      }
    };
    loadCustomFields();
  }, [employee?.id]);

  // Load payslips for the logged-in employee
  useEffect(() => {
    const loadSlips = async () => {
      if (!employee?.id) return;
      try {
        setSlipsLoading(true);
        const res = await listPayslips(employee.id);
        setPayslips(res.data || []);
      } catch (e) {
        console.error('Erreur chargement fiches de paie:', e);
      } finally {
        setSlipsLoading(false);
      }
    };
    loadSlips();
  }, [employee?.id]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        // Use different endpoints based on user role
        const isAdmin = userRole !== null && userRole.toString() === "1";
        
        const [missionsRes, sortiesRes, employeesRes, absencesRes, contratsRes, dechargesRes, leavesRes] = await Promise.all([
          // Missions: admin sees all, user sees only theirs
          isAdmin ? api.get('/mission-orders/count') : api.get('/mission-orders/count'),
          // Exit authorizations: admin sees all, user sees only theirs  
          isAdmin ? api.get('/exit-authorizations/count') : api.get('/exit-authorizations/me/count'),
          // Employees: only admin sees this
          isAdmin ? api.get('/employees/count') : Promise.resolve({ data: 0 }),
          // Absences: only admin sees this
          isAdmin ? api.get('/absences/count') : Promise.resolve({ data: 0 }),
          // Contrats: only admin sees this
          isAdmin ? api.get('/contracts/count') : Promise.resolve({ data: 0 }),
          // Decharges: only admin sees this
          isAdmin ? api.get('/decharge-argents/count') : Promise.resolve({ data: 0 }),
          // Leaves: already correctly handled
          isAdmin ? api.get('/leaves/count') : api.get('/leaves/me/count'),
        ]);
        
        setStats({
          missions: missionsRes.data,
          sorties: sortiesRes.data,
          employees: employeesRes.data,
          absences: absencesRes.data,
          contrats: contratsRes.data,
          decharges: dechargesRes.data,
          leaves: leavesRes.data,
        });
        setError(null);
      } catch (error) {
        console.error('Error loading stats:', error);
        setError('Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [userRole]);

  // Load upcoming holidays (cached) for next 7 days
  useEffect(() => {
    const loadHolidays = async () => {
      try {
        setHolidaysLoading(true);
        const res = await upcomingHolidaysCached(7);
        setHolidaysCache(res.data || { updatedAt: null, days: 7, items: [] });
      } catch (e) {
        console.error('Erreur chargement jours fériés:', e);
      } finally {
        setHolidaysLoading(false);
      }
    };
    loadHolidays();
  }, []);

  // Load leaves needing observation (review queue)
  useEffect(() => {
    const loadReviewQueue = async () => {
      try {
        setReviewLoading(true);
        const res = await reviewQueueLeaves();
        const items = res?.data || [];
        setReviewCount(items.length);
      } catch (e) {
        setReviewCount(0);
      } finally {
        setReviewLoading(false);
      }
    };
    loadReviewQueue();
  }, [userRole, employee?.id]);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoadingDocuments(true);
        const response = await api.get('/documents-legaux'); // Assuming this endpoint returns documents
        setDocumentLegals(response.data);
        setErrorDocuments(null);
      } catch (error) {
        console.error('Error loading legal documents:', error);
        setErrorDocuments('Impossible de charger les documents légaux');
      } finally {
        setLoadingDocuments(false);
      }
    };
    fetchDocuments();
  }, []);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) return '';
    try {
      const formData = new FormData();
      formData.append('files', selectedFile);

      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.filePaths;
    } catch (error) {
      console.error('Error uploading file:', error);
      return '';
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const profilePictureUrl = await handleUpload();
      if (profilePictureUrl) {
        await api.put(`/employees/${employee.id}`, { profile_picture: profilePictureUrl });
        const updatedEmployee = await api.get(`/employees/${employee.id}`);
        setEmployee(updatedEmployee.data);
        handleCloseModal();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  // Custom fields handlers
  const handleCFChange = (name, value) => {
    setCfValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleCFFileUpload = async (name, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('files', file);
      const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.filePaths;
      setCfValues((prev) => ({ ...prev, [name]: url }));
    } catch (e) {
      console.error('Upload fichier (CF) échoué:', e);
    }
  };

  const saveCustomFields = async () => {
    if (!employee?.id) return;
    try {
      setCfSaving(true);
      await updateEmployeeCustomFields(employee.id, cfValues);
      setCfInitial(cfValues);
      setSnack({ open: true, message: 'Informations personnalisées enregistrées', severity: 'success' });
    } catch (e) {
      console.error('Sauvegarde CF échouée:', e);
      setSnack({ open: true, message: 'Erreur lors de la sauvegarde', severity: 'error' });
    } finally {
      setCfSaving(false);
    }
  };

  const isEditableByUser = (meta) => {
    const editableBy = meta?.editableBy || 'admin';
    return editableBy !== 'admin';
  };

  const isDirty = () => {
    const keys = new Set([...Object.keys(cfInitial || {}), ...Object.keys(cfValues || {})]);
    for (const k of keys) {
      if ((cfInitial?.[k] ?? '') !== (cfValues?.[k] ?? '')) return true;
    }
    return false;
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const handleOpenModalDocuments = (pdf) => {
    setSelectedPdf(pdf);
    setIsModalOpen(true);
  };

  const handleCloseModalDocuments = () => {
    setSelectedPdf(null);
    setIsModalOpen(false);
  };

  const StatCard = ({ title, value, icon, color, to }) => (
    <Link
      to={to}
      style={{ textDecoration: 'none' }}
      component="a"
    >
      <Card
        sx={{
          backgroundColor: color,
          color: 'white',
          borderRadius: 2,
          boxShadow: theme.shadows[4],
          transition: 'all 0.3s ease',
          height: '120px',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8],
            cursor: 'pointer',
          },
        }}
      >
        <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', padding: '12px !important' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <div>
              <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 1, fontSize: '0.75rem' }}>
                {title}
              </Typography>
              {loading ? (
                <Skeleton width={60} height={24} />
              ) : (
                <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                  {value}
                </Typography>
              )}
            </div>
            <Box sx={{ opacity: 0.8 }}>
              {icon}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Link>
  );

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result 
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : '255,0,0'; // fallback to red if invalid hex
  };

  const BRAND = useBranding();
  return (
    <Box p={3}>
      {/* Notification: Leaves needing observation */}
      {reviewCount > 0 && (
        <Box mb={2}>
          <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => (window.location.href = '/leaves')}>Voir</Button>}>
            Vous avez {reviewCount} demande{reviewCount > 1 ? 's' : ''} de congé à observer
          </Alert>
        </Box>
      )}
      <Container maxWidth="lg" sx={{ mb: 4 }}>
        {/* Logo removed from center */}
      </Container>

      {userRole !== null && userRole.toString() === "1" ? (
        // Admin view remains the same
        <>
          <Box mb={4}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ 
              background: 'linear-gradient(45deg, #2196F3 30%, #1976D2 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'white',
              fontWeight: 'bold',
              textAlign: 'center',
              py: 2,
              px: 3,
              borderRadius: 2,
              mb: 2
            }}>
              2MP RH PLATEFORME
            </Typography>
          </Box>

          {error && (
            <Box mb={4}>
              <Typography color="error">{error}</Typography>
              <Button
                variant="outlined"
                color="error"
                onClick={() => window.location.reload()}
                startIcon={<RefreshIcon />}
              >
                Réessayer
              </Button>
            </Box>
          )}

          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={1.7}>
              <StatCard
                title="Missions"
                value={stats.missions}
                icon={<AssignmentIcon sx={{ fontSize: 30 }} />}
                color={theme.palette.primary.main}
                to="/mission-orders"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <StatCard
                title="Sorties"
                value={stats.sorties}
                icon={<ExitToAppIcon sx={{ fontSize: 30 }} />}
                color={theme.palette.warning.main}
                to="/exit-authorizations"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <StatCard
                title="Employés"
                value={stats.employees}
                icon={<PeopleIcon sx={{ fontSize: 30 }} />}
                color={theme.palette.secondary.main}
                to="/employees"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <StatCard
                title="Absences"
                value={stats.absences}
                icon={<AssignmentIcon sx={{ fontSize: 30 }} />}
                color={theme.palette.info.main}
                to="/absences"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <StatCard
                title="Contrats"
                value={stats.contrats}
                icon={<WorkIcon sx={{ fontSize: 30 }} />}
                color={theme.palette.secondary.main}
                to="/contracts"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <StatCard
                title="Décharges"
                value={stats.decharges}
                icon={<DirectionsCarIcon sx={{ fontSize: 30 }} />}
                color={theme.palette.success.main}
                to="/decharge-argents"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <StatCard
                title="Congés"
                value={stats.leaves}
                icon={<BeachAccessIcon sx={{ fontSize: 30 }} />}
                color={theme.palette.warning.main}
                to="/leaves"
              />
            </Grid>
          </Grid>
        </>
      ) : (
        <Box textAlign="center" mt={10}>
        <Box position="relative" display="inline-block" mb={3}>
          <Avatar
            src={employee?.profile_picture}
            alt={`${employee?.first_name || employee?.firstName || ''} ${employee?.last_name || employee?.lastName || ''}`}
            sx={{ width: 120, height: 120, mb: 2, border: '4px solid', borderColor: 'primary.main' }}
          >
            {!employee?.profile_picture && <PersonIcon sx={{ fontSize: 60 }} />}
          </Avatar>
          <Tooltip title="Modifier la photo">
            <IconButton
              onClick={() => setOpenModal(true)}
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                backgroundColor: 'primary.main',
                color: 'white',
                width: 40,
                height: 40,
                '&:hover': { backgroundColor: 'primary.dark' }
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Card sx={{ maxWidth: 600, mx: 'auto', borderRadius: 3, boxShadow: theme.shadows[4] }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              👋 Bienvenue {employee?.first_name || employee?.firstName} {employee?.last_name || employee?.lastName}
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
              Voici votre tableau de bord personnel
            </Typography>
            
            <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={2}>
              <Box p={2} borderRadius={2} sx={{ backgroundColor: theme.palette.primary.light, color: 'white', textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="bold">{stats.missions}</Typography>
                <Typography variant="body2">Missions</Typography>
              </Box>
              <Box p={2} borderRadius={2} sx={{ backgroundColor: theme.palette.warning.light, color: 'white', textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="bold">{stats.sorties}</Typography>
                <Typography variant="body2">Sorties</Typography>
              </Box>
              <Box p={2} borderRadius={2} sx={{ backgroundColor: theme.palette.info.light, color: 'white', textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="bold">{stats.absences}</Typography>
                <Typography variant="body2">Absences</Typography>
              </Box>
              <Box p={2} borderRadius={2} sx={{ backgroundColor: theme.palette.secondary.light, color: 'white', textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="bold">{stats.contrats}</Typography>
                <Typography variant="body2">Contrats</Typography>
              </Box>
              <Box p={2} borderRadius={2} sx={{ backgroundColor: theme.palette.success.light, color: 'white', textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="bold">{stats.decharges}</Typography>
                <Typography variant="body2">Décharges</Typography>
              </Box>
              <Box p={2} borderRadius={2} sx={{ backgroundColor: theme.palette.info.light, color: 'white', textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="bold">{stats.leaves}</Typography>
                <Typography variant="body2">Congés</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
      )}

      {/* Upcoming holidays section */}
      <Box mt={4}>
        <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[3] }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              📅 Jours fériés à venir (7 jours)
            </Typography>
            {holidaysLoading ? (
              <Skeleton variant="rectangular" width="100%" height={60} />
            ) : (holidaysCache.items || []).length === 0 ? (
              <Typography color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>Aucun jour férié à venir</Typography>
            ) : (
              <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(280px, 1fr))" gap={2}>
                {(holidaysCache.items || []).map((h) => {
                  const d = new Date(h.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                  return (
                    <Box key={h.id || h.date} p={2} borderRadius={2} border={1} borderColor={theme.palette.divider} sx={{ backgroundColor: theme.palette.grey[50], '&:hover': { backgroundColor: theme.palette.grey[100] } }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        {d}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        {h.name || 'Jour férié'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
            {holidaysCache.updatedAt && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2, textAlign: 'right' }}>
                Mis à jour: {new Date(holidaysCache.updatedAt).toLocaleString('fr-FR')}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Custom Fields section for user */}
      {employee && (
        <Box mt={4}>
          <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[3] }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                👤 Mes informations
              </Typography>
              {cfLoading ? (
                <Skeleton variant="rectangular" width="100%" height={80} />
              ) : Object.keys(cfMeta || {}).length === 0 ? (
                <Typography color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>Aucun champ</Typography>
              ) : (
                <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(320px, 1fr))" gap={2}>
                  {Object.entries(cfMeta).map(([name, meta]) => {
                    const label = meta.label || name;
                    const value = cfValues[name] || '';
                    return (
                      <Box key={name} p={2} borderRadius={2} border={1} borderColor={theme.palette.divider} sx={{ backgroundColor: theme.palette.grey[50] }}>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600, mb: 1 }}>
                          {label}
                        </Typography>
                        {meta.type === 'file' ? (
                          value ? (
                            <Button variant="outlined" size="small" href={value} target="_blank" rel="noopener" sx={{ textTransform: 'none' }}>
                              📄 Voir / Télécharger
                            </Button>
                          ) : (
                            <Typography variant="body2" color="textSecondary">Aucun fichier</Typography>
                          )
                        ) : (
                          <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>{String(value)}</Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Payslips section for user */}
      {employee && (
        <Box mt={4}>
          <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[3] }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                💰 Mes fiches de paie
              </Typography>
              {slipsLoading ? (
                <Skeleton variant="rectangular" width="100%" height={80} />
              ) : (payslips || []).length === 0 ? (
                <Typography color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>Aucune fiche de paie</Typography>
              ) : (
                <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(340px, 1fr))" gap={2}>
                  {(payslips || []).map((s) => {
                    const displayDate = new Date(s.slip_date).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
                    const url = (s.file_path || '').startsWith('http')
                      ? s.file_path
                      : `${(baseURL || '').replace('/api','')}${s.file_path}`;
                    return (
                      <Box key={s.id} p={2} borderRadius={2} border={1} borderColor={theme.palette.divider} sx={{ backgroundColor: theme.palette.grey[50], display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {displayDate}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Fiche de paie
                          </Typography>
                        </Box>
                        <Button size="small" variant="contained" onClick={() => window.open(url, '_blank')} sx={{ minWidth: 'auto' }}>
                          📥 Télécharger
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}
      {/* Documents Légaux section */}
      <Box mt={4}>
        <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[3] }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              📋 Documents Légaux
            </Typography>
            {loadingDocuments ? (
              <Skeleton variant="rectangular" width="100%" height={50} />
            ) : errorDocuments ? (
              <Typography color="error">{errorDocuments}</Typography>
            ) : documentLegals.length === 0 ? (
              <Typography color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>Aucun document légal disponible</Typography>
            ) : (
              <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={2}>
                {documentLegals.map((doc) => (
                  <Button
                    key={doc.id}
                    variant="contained"
                    sx={{
                      backgroundColor: doc.color,
                      color: 'white',
                      fontWeight: 'bold',
                      boxShadow: `0px 4px 10px rgba(${hexToRgb(doc.color)}, 0.3)`,
                      borderRadius: 2,
                      py: 1.5,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: doc.color,
                        boxShadow: `0px 6px 15px rgba(${hexToRgb(doc.color)}, 0.4)`,
                        transform: 'translateY(-2px)'
                      }
                    }}
                    onClick={() => window.open(doc.link, '_blank')}
                  >
                    📄 {doc.name}
                  </Button>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
      <Divider sx={{ my: 4 }} />
      <Box textAlign="center">
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Powered by
        </Typography>
        <img
          src={creatorLogo}
          alt="Creator Company Logo"
          style={{ maxWidth: '100px', height: 'auto' }}
        />
      </Box>

      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>Modifier la photo de profil</DialogTitle>
        <DialogContent>
          <input
            type="file"
            onChange={handleFileChange}
            accept="image/*"
          />
          {selectedFile && (
            <Typography variant="caption" color="textSecondary">
              {selectedFile.name}
            </Typography>
          )}
          {uploadProgress > 0 && (
            <LinearProgress variant="determinate" value={uploadProgress} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Annuler</Button>
          <Button onClick={handleUpdateProfile} color="primary">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DashboardPage;